import React from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LedgerRowsPerPage from './LedgerRowsPerPage';

type LedgerPaginationProps = {
  rowsPerPage: number;
  onRowsPerPageChange: (next: number) => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  disabled?: boolean;
  /** Unique id when multiple ledgers share a page. */
  id?: string;
  /**
   * Optional range label (e.g. "1–5 of 23" or "Page 2").
   * Omitted when the API has no total count and page index alone is not useful.
   */
  label?: React.ReactNode;
};

/**
 * Rows-per-page + Previous/Next for ledger-style lists.
 * Cursor-backed ledgers omit an exact total; pass `label` only when known.
 */
const LedgerPagination: React.FC<LedgerPaginationProps> = ({
  rowsPerPage,
  onRowsPerPageChange,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  disabled = false,
  id = 'ledger-pagination',
  label,
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      <LedgerRowsPerPage
        id={`${id}-rows`}
        value={rowsPerPage}
        onChange={onRowsPerPageChange}
        disabled={disabled}
      />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
        {label != null && label !== '' ? (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={0.25} alignItems="center">
          <IconButton
            size="small"
            onClick={onPrevious}
            disabled={disabled || !canGoPrevious}
            aria-label={t('common.previous', 'Previous')}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNext}
            disabled={disabled || !canGoNext}
            aria-label={t('common.next', 'Next')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default LedgerPagination;
